import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faClipboardCheck,
  faClock,
  faPaperPlane,
  faPaperclip,
  faPlus,
  faSpinner,
  faTicket,
} from "@fortawesome/free-solid-svg-icons";
import Breadcrumb from "../../components/Ui_Element/Breadcrumb";
import { showToast } from "../../components/Ui_Element/CustomToast";
import config from "../../config";
import { getById, post } from "../../contexts/apicontexts";
import TaskComposerModal from "../Tasks/TasksModels/TaskComposerModal";
import { createTask, fetchEmployees, getTaskList } from "../Tasks/taskService";
import {
  formatTaskDate,
  getTaskForwardCount,
  getTaskPriorityClasses,
  getTaskStatusClasses,
  isTaskForwardLocked,
  isTaskFullyCompleted,
} from "../Tasks/taskUtils";
import {
  buildMediaUrl,
  formatDateTime,
  getCurrentUserIdentity,
  getIdentityRoleLabel,
  getPriorityClasses,
  getStatusClasses,
  isCustomerUserIdentity,
  isCustomerIdentity,
  normalizeTicketData,
} from "./ticketUtils";

const BRAND_LR  = "linear-gradient(to right, #C04C2A, #4A1B13)";
const BRAND_135 = "linear-gradient(135deg, #C04C2A 0%, #8B2A15 55%, #4A1B13 100%)";

const statusOptions = ["New", "In Progress", "On Hold", "Resolved"];

export default function TicketDetailsPage() {
  const { id } = useParams();
  const location = useLocation();
  const identity = useMemo(() => getCurrentUserIdentity(), []);
  const identityRoleLabel = useMemo(() => getIdentityRoleLabel(identity), [identity]);
  const isCustomerUserView = useMemo(() => isCustomerUserIdentity(identity), [identity]);
  const backTo = location.state?.backTo || "/tickets";
  const forceViewOnly = Boolean(location.state?.viewOnly);
  const backLabel = String(backTo).startsWith("/assigned-tickets")
    ? "Back to Assigned Tickets"
    : String(backTo).startsWith("/task-history")
      ? "Back to Task History"
      : String(backTo).startsWith("/all-tasks")
        ? "Back to All Task"
        : String(backTo).startsWith("/my-tasks")
          ? "Back to My Tasks"
          : String(backTo).startsWith("/tasks")
            ? "Back to Tasks"
            : "Back to Tickets";

  const [ticket, setTicket] = useState(null);
  const [linkedTasks, setLinkedTasks] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [taskSaving, setTaskSaving] = useState(false);
  const [error, setError] = useState("");
  const [replyMessage, setReplyMessage] = useState("");
  const [replyStatus, setReplyStatus] = useState("");
  const [replyFile, setReplyFile] = useState(null);
  const [isTaskOpen, setIsTaskOpen] = useState(false);
  const canReplyToTicket = Boolean(ticket?.canReply) && !forceViewOnly;
  const canManageTicketStatus = Boolean(ticket?.canManageStatus) && !forceViewOnly;

  const fetchTicket = React.useCallback(async () => {
    setLoading(true); setError("");
    try {
      const response = await getById(`${config.baseurl}Ticket/getById/${id}`);
      if (response?.status === 200) {
        const normalized = normalizeTicketData(response.data);
        setTicket(normalized);
        setReplyStatus(normalized?.status || "");
      } else { setError("Failed to load ticket details."); }
    } catch { setError("Failed to load ticket details."); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { fetchTicket(); }, [fetchTicket]);

  useEffect(() => {
    if (isCustomerIdentity(identity)) return;
    let cancelled = false;
    const loadTaskData = async () => {
      const [taskItems, employeeItems] = await Promise.all([getTaskList({ ticketId: Number(id) }), fetchEmployees()]);
      if (cancelled) return;
      setLinkedTasks(taskItems); setEmployees(employeeItems);
    };
    loadTaskData();
    return () => { cancelled = true; };
  }, [id, identity]);

  const handleReply = async () => {
    if (!canReplyToTicket) { setError("This ticket is view only for your login."); return; }
    const trimmed = replyMessage.trim();
    const statusChanged = canManageTicketStatus && replyStatus && replyStatus !== ticket?.status ? replyStatus : "";
    if (!trimmed && !replyFile && !statusChanged) { setError("Add a reply, attachment, or status change before submitting."); return; }

    if (statusChanged === "Resolved" && !isCustomerIdentity(identity)) {
      try {
        const latestLinkedTasks = await getTaskList({ ticketId: Number(id) });
        setLinkedTasks(latestLinkedTasks);
        const incompleteTasks = latestLinkedTasks.filter((task) => !isTaskFullyCompleted(task));
        if (latestLinkedTasks.length > 0 && incompleteTasks.length > 0) {
          showToast.error(`This ticket cannot be resolved yet. ${incompleteTasks.length} linked ${incompleteTasks.length === 1 ? "task is" : "tasks are"} not completed at 100% progress.`);
          return;
        }
      } catch {
        showToast.error("Linked tasks could not be verified. Please try again before resolving the ticket.");
        return;
      }
    }

    setSaving(true); setError("");
    const formData = new FormData();
    formData.append("TicketId", String(id));
    if (trimmed) formData.append("Message", trimmed);
    if (statusChanged) formData.append("Status", statusChanged);
    if (replyFile) formData.append("AttachmentFile", replyFile);
    try {
      const response = await post(`${config.baseurl}Ticket/reply`, formData);
      if (response?.status === 200) {
        const normalized = normalizeTicketData(response.data);
        setTicket(normalized); setReplyMessage(""); setReplyFile(null); setReplyStatus(normalized?.status || "");
        return;
      }
      setError(response?.message || "Failed to send reply.");
    } catch { setError("Failed to send reply."); }
    finally { setSaving(false); }
  };

  const handleCreateTask = async (values) => {
    if (!ticket) return;
    setTaskSaving(true); setError("");
    try {
      await createTask({
        ...values,
        ticketId: Number(ticket.id || 0),
        ticketNumber: ticket.ticketNumber || "",
        ticketSubject: ticket.subject || "",
        ticketStatus: ticket.status || "",
        sourceType: "SupportTicket",
        createdByUserId: identity.userId,
        createdByName: identity.fullName || identity.email || "User",
      }, identity);
      setIsTaskOpen(false);
      setLinkedTasks(await getTaskList({ ticketId: Number(id) }));
    } catch { setError("Task could not be created from this ticket."); }
    finally { setTaskSaving(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50 p-3 md:p-5">
      <Breadcrumb pageName="Ticket Details" currentPage="Ticket Details" />

      <div className="mt-4 flex items-center">
        <Link
          to={backTo}
          className="inline-flex items-center gap-1.5 rounded-xl border border-orange-100 bg-white px-3 py-2 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-orange-50"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="text-xs" style={{ color: "#C04C2A" }} />
          {backLabel}
        </Link>
      </div>

      {error ? <div className="mt-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-2.5 text-sm text-rose-700">{error}</div> : null}

      {loading ? (
        <div className="mt-5 rounded-2xl border border-dashed border-orange-200 bg-white px-6 py-14 text-center text-sm" style={{ color: "#C04C2A" }}>
          <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
          Loading ticket details…
        </div>
      ) : !ticket ? (
        <div className="mt-5 rounded-2xl border border-dashed border-orange-200 bg-white px-6 py-14 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl text-white" style={{ background: BRAND_LR }}>
            <FontAwesomeIcon icon={faTicket} />
          </div>
          <h2 className="text-base font-bold text-slate-900">Ticket not found</h2>
          <p className="mt-1.5 text-sm text-slate-500">The ticket may not exist or you may not have permission to view it.</p>
        </div>
      ) : (
        <div className="mt-4 grid gap-4 xl:grid-cols-[minmax(0,2fr)_360px]">
          {/* ── Conversation panel ── */}
          <div className="overflow-hidden rounded-2xl border border-orange-100 bg-white shadow-sm" style={{ boxShadow: "0 8px 32px rgba(192,76,42,0.07)" }}>
            {/* Card header */}
            <div className="relative overflow-hidden px-4 py-4 text-white sm:px-5" style={{ background: BRAND_135 }}>
              <div className="pointer-events-none absolute -right-6 -top-6 h-24 w-24 rounded-full bg-white/5 blur-2xl" />
              <div className="relative">
                <div className="flex flex-wrap items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-orange-200">
                  <span>{ticket.ticketNumber}</span>
                  <span className="h-1 w-1 rounded-full bg-orange-200" />
                  <span>{ticket.category}</span>
                </div>
                <h1 className="mt-1 text-base font-extrabold text-white sm:text-lg">{ticket.subject}</h1>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  <span className={`rounded-lg px-2 py-0.5 text-[10px] font-bold ${getStatusClasses(ticket.status)}`}>{ticket.status}</span>
                  <span className={`rounded-lg px-2 py-0.5 text-[10px] font-bold ${getPriorityClasses(ticket.priority)}`}>{ticket.priority}</span>
                  <span className="rounded-lg border border-white/15 bg-white/10 px-2 py-0.5 text-[10px] font-bold text-orange-100">
                    {forceViewOnly ? "Task view only" : canManageTicketStatus ? "Staff ticket view" : canReplyToTicket ? `${identityRoleLabel} reply access` : `${identityRoleLabel} view only`}
                  </span>
                </div>
                <div className="mt-1.5 text-[11px] text-orange-200">
                  Opened {formatDateTime(ticket.createdAt)} · Last activity {formatDateTime(ticket.lastReplyAt || ticket.createdAt)}
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className="space-y-3 bg-slate-50/60 p-4 sm:p-5">
              {(ticket.conversation || []).map((item) => {
                const isMine = Boolean(item.userId) && String(item.userId) === String(identity.userId);
                return (
                  <div key={`${item.id}-${item.createdAt}`} className={`flex ${isMine ? "justify-end" : "justify-start"}`}>
                    <div
                      className="max-w-3xl overflow-hidden rounded-2xl border px-4 py-3 shadow-sm"
                      style={
                        isMine
                          ? { background: BRAND_135, borderColor: "rgba(192,76,42,0.3)" }
                          : { background: "#fff", borderColor: "#f1e8e4" }
                      }
                    >
                      <div className="flex flex-wrap items-center gap-1.5 text-[10px]">
                        <span
                          className="rounded-md px-2 py-0.5 font-bold"
                          style={isMine ? { background: "rgba(255,255,255,0.15)", color: "#fff" } : { background: "#fef2ec", color: "#C04C2A" }}
                        >
                          {item.senderType}
                        </span>
                        {item.status ? (
                          <span className={`rounded-md px-2 py-0.5 font-bold ${getStatusClasses(item.status)}`}>Status: {item.status}</span>
                        ) : null}
                        {item.isInitialMessage ? (
                          <span
                            className="rounded-md px-2 py-0.5 font-bold"
                            style={isMine ? { background: "rgba(255,255,255,0.15)", color: "#fff" } : { background: "#fef9ec", color: "#b45309" }}
                          >
                            Ticket opened
                          </span>
                        ) : null}
                      </div>

                      <div className="mt-2 flex items-start justify-between gap-3">
                        <div>
                          <div className={`text-sm font-bold ${isMine ? "text-white" : "text-slate-900"}`}>{item.senderName}</div>
                          <div className={`text-[10px] ${isMine ? "text-orange-200" : "text-slate-400"}`}>{formatDateTime(item.createdAt)}</div>
                        </div>
                        {item.senderEmail ? (
                          <div className={`text-[11px] ${isMine ? "text-orange-200" : "text-slate-400"}`}>{item.senderEmail}</div>
                        ) : null}
                      </div>

                      {item.message ? (
                        <p className={`mt-3 whitespace-pre-wrap text-sm leading-6 ${isMine ? "text-orange-50" : "text-slate-700"}`}>
                          {item.message}
                        </p>
                      ) : null}

                      {item.attachmentUrl ? (
                        <a
                          href={buildMediaUrl(item.attachmentUrl)}
                          target="_blank"
                          rel="noreferrer"
                          className="mt-3 inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[11px] font-bold transition"
                          style={isMine
                            ? { background: "rgba(255,255,255,0.15)", color: "#fff" }
                            : { background: "#fef2ec", color: "#C04C2A" }}
                        >
                          <FontAwesomeIcon icon={faPaperclip} className="text-[10px]" />
                          View attachment
                        </a>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Sidebar ── */}
          <div className="space-y-4">
            {/* Ticket Info */}
            <div className="overflow-hidden rounded-2xl border border-orange-100 bg-white shadow-sm">
              <div className="border-b border-orange-100 px-4 py-3" style={{ background: "rgba(192,76,42,0.04)" }}>
                <h2 className="text-sm font-extrabold uppercase tracking-widest" style={{ color: "#C04C2A" }}>Ticket Info</h2>
              </div>
              <div className="space-y-2.5 p-4 text-sm">
                <InfoRow label="Organization"    value={ticket.organizationName || "Not provided"} />
                <InfoRow label="Customer"        value={ticket.customerName || "Not provided"} />
                <InfoRow label="Customer Code"   value={ticket.customerCode || "Not provided"} />
                <InfoRow label="Requester"       value={ticket.requesterName || ticket.userName} />
                <InfoRow label="Requester Email" value={ticket.requesterEmail || ticket.contactEmail} />
                <InfoRow label="Contact Email"   value={ticket.contactEmail} />
                <InfoRow label="Domain URL"      value={ticket.domainUrl || "Not provided"} />
                <InfoRow label="Requester ID"    value={ticket.requesterUserId || ticket.userId} mono />
                <InfoRow label="Updated"         value={formatDateTime(ticket.lastReplyAt || ticket.createdAt)} />
              </div>
            </div>

            {/* Linked Tasks */}
            {!isCustomerIdentity(identity) && (
              <div className="overflow-hidden rounded-2xl border border-orange-100 bg-white shadow-sm">
                <div className="flex items-center justify-between border-b border-orange-100 px-4 py-3" style={{ background: "rgba(192,76,42,0.04)" }}>
                  <div>
                    <h2 className="text-sm font-extrabold uppercase tracking-widest" style={{ color: "#C04C2A" }}>Linked Tasks</h2>
                    <p className="mt-0.5 text-[10px] text-slate-400">Internal work items for this ticket.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => { if (!window.confirm(`Create a task from ticket ${ticket.ticketNumber || ""}?`)) return; setIsTaskOpen(true); }}
                    className="inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-[11px] font-bold text-white transition hover:opacity-90"
                    style={{ background: BRAND_LR }}
                  >
                    <FontAwesomeIcon icon={faPlus} />
                    Make Task
                  </button>
                </div>

                {linkedTasks.length ? (
                  <div className="space-y-2 p-3">
                    {linkedTasks.map((task) => (
                      <div key={task.id} className="rounded-xl border border-orange-100 bg-orange-50/30 p-3">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className={`rounded-md px-2 py-0.5 text-[10px] font-bold ${getTaskStatusClasses(task.status)}`}>{task.status}</span>
                          <span className={`rounded-md px-2 py-0.5 text-[10px] font-bold ${getTaskPriorityClasses(task.priority)}`}>{task.priority}</span>
                        </div>
                        <div className="mt-2 text-sm font-semibold text-slate-900">{task.title}</div>
                        <div className="mt-0.5 text-[11px] text-slate-400">Assigned to {task.assignedEmployeeName || "Unassigned"}</div>
                        <div className="mt-2 flex flex-wrap gap-1.5 text-[10px]">
                          <span className="rounded-md border border-slate-200 bg-white px-2 py-0.5 font-bold text-slate-500">
                            {getTaskForwardCount(task)} {getTaskForwardCount(task) === 1 ? "forward" : "forwards"}
                          </span>
                          <span className={`rounded-md px-2 py-0.5 font-bold ${isTaskForwardLocked(task) ? "border border-amber-200 bg-amber-100 text-amber-700" : "border border-emerald-200 bg-emerald-100 text-emerald-700"}`}>
                            {isTaskForwardLocked(task) ? "Forward locked" : "Owner active"}
                          </span>
                        </div>
                        <div className="mt-1.5 text-[11px] text-slate-400">Due {formatTaskDate(task.dueDate)}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="rounded-xl border border-dashed border-orange-100 px-4 py-7 text-center text-sm text-slate-400 m-3">
                    <FontAwesomeIcon icon={faClipboardCheck} className="mr-2" style={{ color: "#C04C2A" }} />
                    No task created from this ticket yet.
                  </div>
                )}
              </div>
            )}

            {/* Add Reply */}
            <div className="overflow-hidden rounded-2xl border border-orange-100 bg-white shadow-sm">
              <div className="flex items-center justify-between border-b border-orange-100 px-4 py-3" style={{ background: "rgba(192,76,42,0.04)" }}>
                <div>
                  <h2 className="text-sm font-extrabold uppercase tracking-widest" style={{ color: "#C04C2A" }}>Add Reply</h2>
                  <p className="mt-0.5 text-[10px] text-slate-400">
                    {canReplyToTicket ? "Continue the conversation or update the status." : "Replies are disabled for this login."}
                  </p>
                </div>
                <span className="inline-flex items-center gap-1.5 rounded-xl border border-orange-100 bg-orange-50 px-2.5 py-1 text-[10px] font-bold text-slate-600">
                  <FontAwesomeIcon icon={faClock} style={{ color: "#C04C2A" }} />
                  {forceViewOnly ? "Task view only" : canManageTicketStatus ? "Staff view" : canReplyToTicket ? "Reply allowed" : "View only"}
                </span>
              </div>

              {canReplyToTicket ? (
                <div className="space-y-3 p-4">
                  {canManageTicketStatus ? (
                    <div>
                      <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-widest text-slate-400">Status</label>
                      <select
                        value={replyStatus}
                        onChange={(e) => setReplyStatus(e.target.value)}
                        className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none transition"
                        onFocus={(e) => { e.target.style.borderColor = "#C04C2A"; }}
                        onBlur={(e)  => { e.target.style.borderColor = ""; }}
                      >
                        {statusOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  ) : null}

                  <div>
                    <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-widest text-slate-400">Reply</label>
                    <textarea
                      value={replyMessage}
                      onChange={(e) => setReplyMessage(e.target.value)}
                      className="min-h-[120px] w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none transition"
                      onFocus={(e) => { e.target.style.borderColor = "#C04C2A"; e.target.style.boxShadow = "0 0 0 3px rgba(192,76,42,0.10)"; }}
                      onBlur={(e)  => { e.target.style.borderColor = ""; e.target.style.boxShadow = ""; }}
                      placeholder="Write your update, answer, or follow-up…"
                    />
                  </div>

                  <div>
                    <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-widest text-slate-400">Attachment</label>
                    <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-dashed border-orange-200 bg-orange-50/30 px-3 py-3 transition hover:bg-orange-50">
                      <FontAwesomeIcon icon={faPaperclip} style={{ color: "#C04C2A" }} />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-semibold text-slate-700">{replyFile ? replyFile.name : "Attach supporting file"}</div>
                        <div className="text-[10px] text-slate-400">Optional for reply or status update</div>
                      </div>
                      <input type="file" className="hidden" onChange={(e) => setReplyFile(e.target.files?.[0] || null)} />
                    </label>
                  </div>

                  <button
                    type="button"
                    onClick={handleReply}
                    disabled={saving}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-bold text-white transition hover:opacity-90 disabled:opacity-70"
                    style={{ background: BRAND_LR }}
                  >
                    <FontAwesomeIcon icon={saving ? faSpinner : faPaperPlane} spin={saving} />
                    {saving ? "Sending…" : "Add Reply"}
                  </button>
                </div>
              ) : (
                <div className="m-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3.5 text-sm text-amber-800">
                  {forceViewOnly
                    ? "This ticket was opened in view-only mode. You can review the conversation but replying is disabled."
                    : isCustomerUserView
                      ? "This ticket was created by another user under your customer account. Only the original requester or support staff can reply."
                      : "Replies are not available for this ticket on your current access level."}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <TaskComposerModal
        open={isTaskOpen}
        ticket={ticket}
        employees={employees}
        identity={identity}
        submitting={taskSaving}
        onClose={() => setIsTaskOpen(false)}
        onSubmit={handleCreateTask}
      />
    </div>
  );
}

function InfoRow({ label, value, mono }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-slate-100 pb-2.5 last:border-b-0 last:pb-0">
      <span className="shrink-0 text-slate-400">{label}</span>
      <span className={`text-right font-semibold text-slate-800 ${mono ? "font-mono text-xs" : "text-sm"}`}>{value}</span>
    </div>
  );
}
